import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Leaf, TreePine, ChevronRight, Info, AlertCircle, RefreshCw } from 'lucide-react';
import logger from "@/utils/logger";

// ═══════════════════════════════════════════════════════════════════════
// COMPLETE ONBOARDING QUESTIONS
// Merges: Timeline + Lifestyle + Medical Context into ONE conversational flow
// ═══════════════════════════════════════════════════════════════════════

interface OnboardingQuestion {
    id: string;
    question: string;
    type: 'timeline' | 'single-select' | 'multi-select' | 'scale' | 'demographics' | 'multi-input' | 'body-metrics' | 'transition';
    category: 'pattern' | 'passport'; // pattern = onboarding_profiles, passport = user_profiles
    prakriti_factor?: boolean; // added for constitutional detection
    options?: Array<{
        value: string;
        label: string;
        emoji?: string;
        description?: string;
    }>;
    fields?: Array<{
        id: string;
        label: string;
        type: 'text' | 'number' | 'select';
        options?: Array<string | { label: string; value: string }>;
        placeholder?: string;
        unit?: string;
    }>;
    placeholder?: string;
    teaching: string;
    allowSkip?: boolean;
    followUp?: Record<string, string>; // conditional next question based on answer
    bullets?: string[];
    maxSelect?: number;
}

const COMPLETE_ONBOARDING_QUESTIONS: OnboardingQuestion[] = [
    // ═══════════════════════════════════════════════════════════════════════
    // SECTION 1: NATURE / PRAKRITI (Baseline Patterns)
    // ═══════════════════════════════════════════════════════════════════════
    {
        id: 'energy_pattern',
        question: "Throughout your life — not just lately — when do you naturally have the most energy? Think about how you've always been.",
        type: 'single-select',
        category: 'pattern',
        prakriti_factor: true,
        options: [
            { value: 'morning', label: 'Morning (I\'m a morning person)', emoji: '🌅' },
            { value: 'midday', label: 'Midday/afternoon', emoji: '☀️' },
            { value: 'evening', label: 'Evening/night', emoji: '🌙' },
            { value: 'inconsistent', label: 'It varies day to day', emoji: '🎲' },
        ],
        teaching: "Natural energy rhythms reveal your constitutional tendencies — this is one of the most reliable indicators of your Prakriti.",
    },
    {
        id: 'primary_goal',
        question: "What aspect of your wellbeing matters most to you?", // Updated text
        type: 'single-select',
        category: 'passport',
        teaching: "Your primary goal helps us focus our recommendations. We start by addressing your most pressing need.",
    },
    {
        id: 'weather_sensitivity',
        question: "Across your life, have you naturally tended to feel better or worse in certain weather? This is about your constitutional sensitivity, not just right now.",
        type: 'single-select',
        category: 'pattern',
        prakriti_factor: true,
        options: [
            { value: 'cold-dry', label: 'Worse in cold/dry weather', emoji: '❄️' },
            { value: 'hot-humid', label: 'Worse in hot/humid weather', emoji: '🔥' },
            { value: 'rainy-damp', label: 'Worse in rainy/damp weather', emoji: '🌧️' },
            { value: 'no-connection', label: 'No weather connection noticed', emoji: '🤷' },
        ],
        teaching: "Weather sensitivity shows how external elements affect your internal balance. Classic Ayurvedic diagnosis.",
    },
    {
        id: 'body_frame',
        question: "Think about your natural body build — not what it is right now, but how you've always naturally tended to be.",
        type: 'single-select',
        category: 'pattern',
        prakriti_factor: true,
        options: [
            { value: 'thin-light', label: 'Thin/light/hard to gain weight', emoji: '🦴' },
            { value: 'medium-muscular', label: 'Medium/muscular/moderate build', emoji: '💪' },
            { value: 'large-soft', label: 'Larger/soft/easy to gain weight', emoji: '⚖️' },
        ],
        teaching: "Body frame is the most direct window into your Prakriti — Charaka Samhita uses this as the primary constitutional indicator.",
    },
    {
        id: 'natural_digestion',
        question: "When you're eating well and life is balanced, how does your digestion naturally work? Not lately — your natural baseline.",
        type: 'single-select',
        category: 'pattern',
        prakriti_factor: true,
        options: [
            { value: 'variable', label: 'Variable/irregular/gas-prone', emoji: '🎲' },
            { value: 'strong', label: 'Strong/sharp/frequent hunger', emoji: '🔥' },
            { value: 'slow', label: 'Slow/steady/heavy feeling', emoji: '🐢' },
        ],
        teaching: "Agni — your digestive fire — is constitutional. Vata = variable, Pitta = sharp, Kapha = slow. This rarely changes.",
    },
    {
        id: 'skin_hair_type',
        question: "How would you describe your skin and hair naturally — not due to current weather, stress, or products?",
        type: 'single-select',
        category: 'pattern',
        prakriti_factor: true,
        options: [
            { value: 'dry-rough', label: 'Dry/rough/thin hair', emoji: '🏜️' },
            { value: 'oily-sensitive', label: 'Oily/sensitive/prone to redness', emoji: '🍎' },
            { value: 'thick-oily', label: 'Thick/oily/lustrous', emoji: '🌊' },
        ],
        teaching: "Skin and hair reflect your elemental composition. These physical traits are stable markers of your underlying constitution.",
    },
    {
        id: 'natural_temperament',
        question: "At your core, when life is going well, how would you describe your natural personality?",
        type: 'single-select',
        category: 'pattern',
        prakriti_factor: true,
        options: [
            { value: 'creative', label: 'Creative/enthusiastic/changeable/anxious', emoji: '🎨' },
            { value: 'focused', label: 'Focused/driven/sharp/intense', emoji: '🎯' },
            { value: 'calm', label: 'Calm/steady/nurturing/content', emoji: '🧘' },
        ],
        teaching: "In Ayurveda, mind and body share the same constitution. Your natural mental tendencies are as diagnostic as your physical ones.",
    },

    // ═══════════════════════════════════════════════════════════════════════
    // SECTION 2: CURRENT STATE / VIKRITI (Right Now)
    // ═══════════════════════════════════════════════════════════════════════
    {
        id: '_transition_vikriti',
        question: "How are you feeling right now?",
        type: 'transition',
        category: 'pattern',
        teaching: "Now let's look at what your body is experiencing right now.",
    },
    {
        id: '_transition_prakriti',
        question: "Now let's discover your lifelong nature.",
        type: 'transition',
        category: 'pattern',
        teaching: "This is your Prakriti — the unique combination of the 5 elements you were born with. Unlike your current symptoms, your Prakriti is stable and reveals how your body naturally stays in balance.",
    },
    {
        id: 'onset',
        question: "When did you first notice these patterns in your body?",
        type: 'timeline',
        category: 'pattern',
        options: [
            { value: 'today-yesterday', label: 'Today or yesterday', emoji: '🆕' },
            { value: 'this-week', label: 'This week', emoji: '📅' },
            { value: '2-4-weeks', label: '2-4 weeks ago', emoji: '🗓️' },
            { value: '1-3-months', label: '1-3 months ago', emoji: '📆' },
            { value: '3-months-plus', label: 'Longer than 3 months', emoji: '⏳' },
        ],
        teaching: "Timing helps us understand if this is acute (recent) or chronic (long-standing). Each requires different approaches.",
        followUp: {
            '2-4-weeks': 'life_events',
            '1-3-months': 'life_events',
            '3-months-plus': 'life_events'
        }
    },
    {
        id: 'life_events',
        question: "What else was happening around that time? (Select all that apply)",
        type: 'multi-select',
        category: 'pattern',
        options: [
            { value: 'diet-change', label: 'Changed diet or eating schedule', emoji: '🥗' },
            { value: 'exercise-change', label: 'Started or stopped exercising', emoji: '🏋️' },
            { value: 'stress-event', label: 'Experienced stress or life changes', emoji: '📊' },
            { value: 'sleep-change', label: 'Changed sleep patterns', emoji: '😴' },
            { value: 'season-change', label: 'Weather/season shifted', emoji: '🍂' },
            { value: 'travel', label: 'Travel or location change', emoji: '✈️' },
            { value: 'none', label: 'Nothing significant', emoji: '🤷' },
        ],
        teaching: "Your body doesn't exist in isolation. Life events often trigger physical imbalances.",
    },
    {
        id: 'meal_timing',
        question: "When you eat, how regular are your meal times?",
        type: 'scale',
        category: 'pattern',
        options: [
            { value: 'very-regular', label: 'Very regular', emoji: '⏰', description: 'Same times every day' },
            { value: 'somewhat-regular', label: 'Somewhat regular', emoji: '📅', description: 'Usually consistent' },
            { value: 'irregular', label: 'Irregular', emoji: '🔀', description: 'When convenient' },
            { value: 'very-irregular', label: 'Very irregular', emoji: '❓', description: 'Skip meals often' },
        ],
        teaching: "Your body loves rhythm. Regular meal times help maintain your digestive fire (Agni) and balance your doshas.",
    },
    {
        id: 'meal_feeling',
        question: "After eating, how do you typically feel?",
        type: 'single-select',
        category: 'pattern',
        options: [
            { value: 'energized', label: 'Energized & satisfied', emoji: '✨' },
            { value: 'tired', label: 'Tired or sluggish', emoji: '😴' },
            { value: 'bloated', label: 'Uncomfortable or bloated', emoji: '🎈' },
            { value: 'hungry', label: 'Still hungry shortly after', emoji: '🍽️' },
        ],
        teaching: "How you feel after eating reveals your digestive strength. Strong digestion leaves you energized, not depleted.",
    },
    {
        id: 'sleep_pattern',
        question: "How would you describe your sleep lately?",
        type: 'single-select',
        category: 'pattern',
        options: [
            { value: 'Restful', label: 'Deep & refreshing', emoji: '😴' },
            { value: 'Trouble falling asleep', label: 'Trouble falling asleep', emoji: '🌙' },
            { value: 'Waking up often', label: 'Wake frequently', emoji: '⏰' },
            { value: 'Early riser', label: 'Wake too early, can\'t return to sleep', emoji: '🌅' },
            { value: 'Insomnia', label: 'Light & restless', emoji: '🌊' },
        ],
        teaching: "Sleep quality is deeply connected to dosha balance. Vata causes restlessness, Pitta causes waking at 2-4am.",
    },
    {
        id: 'activity_level',
        question: "How active is your lifestyle right now?",
        type: 'scale',
        category: 'pattern',
        options: [
            { value: '2', label: 'Sedentary', emoji: '🪑', description: 'Mostly sitting' },
            { value: '5', label: 'Moderate', emoji: '🚶', description: 'Regular walking/light activity' },
            { value: '8', label: 'Active', emoji: '🏃', description: 'Daily exercise/physical work' },
            { value: '10', label: 'High Intensity', emoji: '🏋️', description: 'Athlete/Heavy physical labor' },
        ],
        teaching: "Your activity level dictates your metabolic rate (Agni) and the amount of stability your body needs.",
    },
    {
        id: 'stress_level',
        question: "How has your inner world been lately? (Select all that apply)",
        type: 'multi-select',
        category: 'pattern',
        options: [
            { value: 'overwhelmed', label: 'Feeling overwhelmed', emoji: '😰' },
            { value: 'low', label: 'Feeling low or unmotivated', emoji: '😔' },
            { value: 'frustrated', label: 'Feeling frustrated or irritable', emoji: '😤' },
            { value: 'worried', label: 'Feeling worried about the future', emoji: '😟' },
            { value: 'balanced', label: 'Generally balanced and content', emoji: '😊' },
        ],
        teaching: "Emotions directly impact physical health in Ayurveda. Worry affects digestion, frustration affects liver/blood.",
    },
    {
        id: 'stress_intensity',
        question: "On a scale of Low to Extreme, what is your current stress intensity?",
        type: 'scale',
        category: 'pattern',
        options: [
            { value: '2', label: 'Low', emoji: '🧘' },
            { value: '5', label: 'Moderate', emoji: '😐' },
            { value: '8', label: 'High', emoji: '🌪️' },
            { value: '10', label: 'Extreme', emoji: '🆘' },
        ],
        teaching: "Quantifying stress helps Nani prioritize calming versus invigorating rituals in your daily blueprint.",
    },

    // ═══════════════════════════════════════════════════════════════════════
    // SECTION 3: ESSENTIAL MEDICAL CONTEXT (Passport Data)
    // ═══════════════════════════════════════════════════════════════════════
    {
        id: '_transition_medical',
        question: "Essential details",
        type: 'transition',
        category: 'passport',
        teaching: "Before we begin your wellness assessment, Nani needs a few details to give you safe, accurate guidance.",
        bullets: [
            "Calculate the right herb dosages for your body",
            "Check for contraindications with your medications or allergies",
            "Personalise meal plans to your dietary needs"
        ]
    },
    {
        id: 'age_sex',
        question: "Help us understand your body better",
        type: 'demographics',
        category: 'passport',
        fields: [
            {
                id: 'age',
                label: 'Your age',
                type: 'number',
                placeholder: '34'
            },
            {
                id: 'sex',
                label: 'Sex assigned at birth',
                type: 'select',
                options: [
                    { label: 'Female', value: 'female' },
                    { label: 'Male', value: 'male' },
                    { label: 'Intersex / Non-binary', value: 'intersex_nonbinary' },
                    { label: 'Prefer not to say', value: 'prefer_not_to_say' }
                ]
            },
        ],
        teaching: "Age and biological sex influence dosha balance. Women's hormonal cycles affect Pitta, while age naturally increases Vata.",
    },
    {
        id: 'height_weight',
        question: "What's your current height and weight?",
        type: 'body-metrics',
        category: 'passport',
        fields: [
            {
                id: 'height',
                label: 'Height',
                type: 'number',
                unit: 'cm',
                placeholder: '165'
            },
            {
                id: 'weight',
                label: 'Weight',
                type: 'number',
                unit: 'kg',
                placeholder: '65'
            },
        ],
        teaching: "Body composition affects how doshas manifest. We use this to calculate safe herb dosages and constitutional tendencies.",
        allowSkip: true,
    },
    {
        id: 'occupation',
        question: "What best describes your work and daily routine?",
        type: 'single-select',
        category: 'passport',
        options: [
            { value: 'desk_sedentary', label: 'Desk-based / sedentary', emoji: '💻' },
            { value: 'physical_labor', label: 'Physical / manual work', emoji: '⚒️' },
            { value: 'high_stress_leadership', label: 'High-stress / leadership', emoji: '⚡' },
            { value: 'shift_irregular', label: 'Shift work / irregular hours', emoji: '🕒' },
            { value: 'travel_fieldwork', label: 'Travel-heavy / field work', emoji: '🌍' },
            { value: 'standing_retail_teaching', label: 'Standing / retail / teaching', emoji: '🏫' },
            { value: 'creative_freelance', label: 'Creative / freelance', emoji: '🧩' },
        ],
        teaching: "Your occupation and daily activity levels are major influencers of your current Vikriti. Long hours in one posture or high-stress environments create specific elemental imbalances.",
    },
    {
        id: 'dietary_preference',
        question: "How would you describe your current diet?",
        type: 'single-select',
        category: 'passport',
        options: [
            { value: 'Omnivore', label: 'Omnivore (eat everything)', emoji: '🍽️' },
            { value: 'Vegetarian', label: 'Vegetarian', emoji: '🥗' },
            { value: 'Vegan', label: 'Vegan', emoji: '🌱' },
            { value: 'Pescatarian', label: 'Pescatarian (fish but no meat)', emoji: '🐟' },
            { value: 'Paleo', label: 'Paleo', emoji: '🥩' },
            { value: 'Keto', label: 'Keto/Low-carb', emoji: '🥑' },
            { value: 'Ayurvedic', label: 'Saatvik Ayurvedic', emoji: '🙏' },
        ],
        teaching: "We'll tailor meal recommendations to your dietary preferences. Note: Ayurveda recommends avoiding red meat and poultry for optimal purity and holistic balance.",
    },
    {
        id: 'allergies',
        question: "Do you have any known allergies? (especially to foods, herbs, or medications)",
        type: 'multi-input',
        category: 'passport',
        placeholder: "Type an allergy and press Enter (e.g., Peanuts, Shellfish, Penicillin)",
        teaching: "This is critical for safety. Many Ayurvedic remedies use herbs that can cross-react with common allergies.",
        allowSkip: true,
    },
    {
        id: 'current_medications',
        question: "Are you currently taking any medications or supplements?",
        type: 'multi-input',
        category: 'passport',
        placeholder: "Type a medication and press Enter (e.g., Levothyroxine 50mcg, Vitamin D 2000IU)",
        teaching: "Herbs can interact with medications. We need to know this to recommend safe combinations.",
        allowSkip: true,
    },
    {
        id: 'medical_history',
        question: "Have you been diagnosed with any ongoing health conditions?",
        type: 'multi-select',
        category: 'passport',
        options: [
            { value: 'diabetes', label: 'Diabetes', emoji: '🩺' },
            { value: 'hypertension', label: 'High Blood Pressure', emoji: '❤️' },
            { value: 'heart-disease', label: 'Heart Disease', emoji: '💔' },
            { value: 'thyroid', label: 'Thyroid Issues', emoji: '🦋' },
            { value: 'anxiety', label: 'Anxiety', emoji: '😰' },
            { value: 'depression', label: 'Depression', emoji: '😔' },
            { value: 'insomnia', label: 'Insomnia', emoji: '😴' },
            { value: 'asthma', label: 'Asthma', emoji: '🫁' },
            { value: 'pcos', label: 'PCOS', emoji: '🌸' },
            { value: 'arthritis', label: 'Arthritis', emoji: '🦴' },
            { value: 'ibs', label: 'IBS/IBD', emoji: '🔄' },
            { value: 'autoimmune', label: 'Autoimmune Condition', emoji: '🛡️' },
            { value: 'none', label: 'None of these', emoji: '✅' },
        ],
        teaching: "Understanding your health history helps us avoid contraindications and target root causes effectively.",
        allowSkip: true,
    },
    {
        id: 'life_stage',
        question: "What is your current life stage regarding your hormonal health?",
        type: 'single-select',
        category: 'passport',
        options: [
            { value: 'puberty', label: 'Menarche / Puberty', emoji: '🌱' },
            { value: 'reproductive', label: 'Active Reproductive / Regular Cycles', emoji: '🌸' },
            { value: 'pregnant', label: 'Pregnancy', emoji: '🤰' },
            { value: 'postpartum', label: 'Postpartum / Nursing', emoji: '🤱' },
            { value: 'perimenopause', label: 'Perimenopause', emoji: '🍂' },
            { value: 'menopause', label: 'Post-menopause', emoji: '❄️' },
        ],
        teaching: "Your life stage dictates your hormonal blueprint, which significantly influences your Dosha balance and nutritional needs.",
    },
    {
        id: 'menstrual_pattern',
        question: "How would you describe your typical menstrual pattern? (Select up to 2)",
        type: 'multi-select',
        category: 'passport',
        maxSelect: 2,
        options: [
            { value: 'regular', label: 'Regular', emoji: '📅' },
            { value: 'irregular', label: 'Irregular', emoji: '🎲' },
            { value: 'heavy_clotted', label: 'Heavy / Clotted', emoji: '🌊' },
            { value: 'light_spotting', label: 'Light / Spotting', emoji: '💧' },
            { value: 'painful_cramps', label: 'Painful / Cramps', emoji: '⚡' },
            { value: 'absent', label: 'Absent / Amenorrhea', emoji: '🚫' },
        ],
        teaching: "In Ayurveda, the menstrual cycle is a 'monthly detox' and a direct window into the health of your Rasa and Rakta dhatus (plasma and blood).",
    },
    {
        id: 'hormonal_context',
        question: "Are you using any hormonal support or contraception? (Select all that apply)",
        type: 'multi-select',
        category: 'passport',
        options: [
            { value: 'none', label: 'Natural / No hormonal support', emoji: '🍃' },
            { value: 'pill_combined', label: 'Combined Pill (OCP)', emoji: '💊' },
            { value: 'pill_progestin', label: 'Progestogen-only / Mini-pill', emoji: '🧬' },
            { value: 'iud_hormonal', label: 'Hormonal IUD (Mirena/Kyleena)', emoji: '⚓' },
            { value: 'iud_copper', label: 'Copper IUD (Non-hormonal)', emoji: '🎖️' },
            { value: 'implant_injection', label: 'Implant / Injection', emoji: '💉' },
            { value: 'hrt', label: 'HRT / MHT (Menopause Support)', emoji: '✨' },
            { value: 'pcos_endo_treatment', label: 'PCOS / Endometriosis Treatment', emoji: '🌸' },
        ],
        teaching: "Hormonal medications can interact with certain herbs. We need this for the safety of your Botanical Rx.",
    },
    {
        id: 'flow_color',
        question: "What is the typical color of your menstrual flow? (Optional)",
        type: 'single-select',
        category: 'passport',
        allowSkip: true,
        options: [
            { value: 'color_bright_red', label: 'Bright Red', emoji: '🔴' },
            { value: 'color_dark_red', label: 'Dark Red / Purple', emoji: '🟣' },
            { value: 'color_pink_pale', label: 'Pink / Pale', emoji: '🌸' },
            { value: 'color_brown_dark', label: 'Brownish / Dark', emoji: '🟤' },
            { value: 'color_orange', label: 'Orange / Unusual', emoji: '🟠' },
        ],
        teaching: "Flow color is a diagnostic tool in Ayurveda: Bright red = Pitta, Dark/Clotted = Kapha, Brown/Scanty = Vata.",
    },
];

// ═══════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════

interface OnboardingFlowProps {
    userIntent?: 'wellness' | 'condition';
    primaryConcern?: string | null;
    affectedSystem?: 'digestive' | 'musculoskeletal' | 'general';
    initialData?: Record<string, any>;
    skipAnswered?: boolean;
    isRefreshMode?: boolean;
    segment?: 'prakriti' | 'prakriti-transition' | 'vikriti' | 'vikriti-transition' | 'vikriti-questions' | 'passport' | 'primary-goal' | 'all';
    completedSegments?: string[];
    skippedSegments?: string[];
    vikritiSkipped?: boolean;
    onPrakritiComplete?: (data: {
        answers: Record<string, any>;
        patternData: Record<string, any>;
        passportData: Record<string, any>;
        vikritiSkipped: boolean;
    }) => void | Promise<void>;
    onVikritiComplete?: (data: {
        answers: Record<string, any>;
        patternData: Record<string, any>;
        passportData: Record<string, any>;
        vikritiSkipped: boolean;
    }) => void | Promise<void>;
    onComplete: (data: {
        patternData: Record<string, any>;
        passportData: Record<string, any>;
        vikritiSkipped: boolean;
        prakritiSkipped?: boolean;
        answers: Record<string, any>;
    }) => void | Promise<void>;
    onSegmentNavigate?: (segment: string) => void;
}

export default function OnboardingFlow({
    userIntent = 'wellness',
    primaryConcern = null,
    affectedSystem = 'general',
    initialData = {},
    skipAnswered = false,
    isRefreshMode = false,
    segment = 'all',
    completedSegments = [],
    skippedSegments = [],
    vikritiSkipped = false,
    onPrakritiComplete,
    onVikritiComplete,
    onComplete,
    onSegmentNavigate
}: OnboardingFlowProps) {
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState<Record<string, any>>(initialData);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [questionFlow, setQuestionFlow] = useState<OnboardingQuestion[]>([]);
    const [showTeaching, setShowTeaching] = useState(false);
    const [localVikritiSkipped, setLocalVikritiSkipped] = useState(vikritiSkipped || false);

    // Build question flow on mount/update
    React.useEffect(() => {
        const prakritiQs = COMPLETE_ONBOARDING_QUESTIONS.filter(q => q.prakriti_factor);
        const transitionVikritiQ = COMPLETE_ONBOARDING_QUESTIONS.find(q => q.id === '_transition_vikriti')!;
        const vikritiQs = COMPLETE_ONBOARDING_QUESTIONS.filter(q => 
            q.category === 'pattern' && !q.prakriti_factor && !q.id.startsWith('_transition')
        );
        const medicalTransitionQ = COMPLETE_ONBOARDING_QUESTIONS.find(q => q.id === '_transition_medical')!;
        const whQuestionIds = ['life_stage', 'menstrual_pattern', 'hormonal_context', 'flow_color'];
        const passportQs = COMPLETE_ONBOARDING_QUESTIONS.filter(q => 
            q.category === 'passport' && 
            q.id !== 'primary_goal' && 
            q.id !== '_transition_medical' &&
            !whQuestionIds.includes(q.id)
        );

        const primaryGoalQ = COMPLETE_ONBOARDING_QUESTIONS.find(q => q.id === 'primary_goal')!;

        let flow: OnboardingQuestion[] = [];

        if (segment === 'primary-goal') {
            flow = [primaryGoalQ];
        } else if (segment === 'prakriti') {
            flow = prakritiQs;
        } else if (segment === 'passport') {
            flow = [medicalTransitionQ, ...passportQs];
            
            // Dynamic injection for initial female data
            const sex = initialData.age_sex?.sex || initialData.sex;
            if (sex === 'female') {
                const whQs = ['life_stage', 'menstrual_pattern', 'hormonal_context', 'flow_color']
                    .map(id => COMPLETE_ONBOARDING_QUESTIONS.find(q => q.id === id))
                    .filter(Boolean) as any[];
                
                const ageSexIdx = flow.findIndex(q => q.id === 'age_sex');
                if (ageSexIdx !== -1) {
                    flow.splice(ageSexIdx + 1, 0, ...whQs);
                }

                // Initial hiding for menstrual pattern
                if (['pregnant', 'postpartum', 'menopause'].includes(initialData.life_stage)) {
                    flow = flow.filter(q => q.id !== 'menstrual_pattern');
                }
            }
        } else if (segment === 'vikriti-transition') {
            flow = [transitionVikritiQ];
        } else if (segment === 'prakriti-transition') {
            const transitionPrakritiQ = COMPLETE_ONBOARDING_QUESTIONS.find(q => q.id === '_transition_prakriti')!;
            flow = [transitionPrakritiQ];
        } else if (segment === 'vikriti-questions') {
            flow = [...vikritiQs]; // Fix 1: Removed medicalTransition and passportQs from here
        } else if (segment === 'vikriti') {
            flow = [transitionVikritiQ, ...vikritiQs];
        } else {
            flow = [...prakritiQs, transitionVikritiQ, ...vikritiQs, medicalTransitionQ, ...passportQs];
        }

        // Filter out answered if requested
        if (skipAnswered && (segment !== 'passport' && segment !== 'vikriti-transition')) {
            flow = flow.filter(q => {
                const val = initialData[q.id];
                if (q.id.startsWith('_transition')) return true;
                if (val === undefined || val === null || val === '') return true;
                return false;
            });
        }

        setQuestionFlow(flow);
        setCurrentQuestionIndex(0); 

        // Fix: Auto-advance if flow is empty
        if (flow.length === 0 && (segment === 'prakriti' || segment === 'vikriti-questions')) {
            onComplete({ patternData: answers, passportData: answers, vikritiSkipped: segment === 'prakriti' ? (vikritiSkipped || localVikritiSkipped) : false, answers: answers });
        }
    }, [segment, skipAnswered, initialData, vikritiSkipped, localVikritiSkipped, onComplete]); 

    const currentQuestion = questionFlow[currentQuestionIndex];
    
    // Fix 5: Progress calculation based on confirmed answers
    // In prakriti or vikriti-questions segments, we want progress to reflect actual completions
    const calculateProgress = () => {
        if (!questionFlow.length) return 0;
        return (currentQuestionIndex / questionFlow.length) * 100;
    };
    const currentSegmentProgress = calculateProgress();

        // Overall Progress Calculation: (S0: 10%, S1: 40%, S2: 50%)
        const calculateOverallProgress = () => {
            let s0 = (completedSegments.includes('passport') || segment === 'passport') ? (segment === 'passport' ? currentSegmentProgress : 100) : 0;
            let s1 = (completedSegments.includes('vikriti') || skippedSegments.includes('vikriti') || vikritiSkipped || localVikritiSkipped || segment?.startsWith('vikriti')) 
                ? (segment?.startsWith('vikriti') ? currentSegmentProgress : 100) : 0;
            let s2 = (completedSegments.includes('prakriti') || segment === 'prakriti') ? (segment === 'prakriti' ? currentSegmentProgress : 100) : 0;
            
            // Handle Refresh Mode logic
            if (isRefreshMode) {
                if (segment !== 'passport') s0 = 100;
                if (segment !== 'prakriti') s2 = 100;
            }
            
            // Cap each segment at 100 before weighting
            const totalProgress = Math.min(100, Math.round((0.1 * Math.min(100, s0)) + (0.4 * Math.min(100, s1)) + (0.5 * Math.min(100, s2))));
            return totalProgress;
        };
    const overallProgress = calculateOverallProgress();

    const handleAnswer = (value: any, isMultiSelect = false) => {
        let newAnswers = { ...answers };

        if (isMultiSelect) {
            const currentValues = newAnswers[currentQuestion.id] || [];
            if (currentValues.includes(value)) {
                newAnswers[currentQuestion.id] = currentValues.filter((v: any) => v !== value);
            } else {
                // Support for maxSelect
                if (currentQuestion.maxSelect && currentValues.length >= currentQuestion.maxSelect) {
                    return;
                }
                newAnswers[currentQuestion.id] = [...currentValues, value];
            }
        } else {
            newAnswers[currentQuestion.id] = value;
        }

        setAnswers(newAnswers);

        // Show teaching moment
        setShowTeaching(true);
        setTimeout(() => setShowTeaching(false), 4000);
    };

    const handleNext = () => {
        if (!currentQuestion) return;

        // Custom validation
        if (currentQuestion.id === 'age_sex') {
            const ageVal = answers[currentQuestion.id]?.age;
            if (ageVal !== undefined && ageVal !== null && ageVal !== '') {
                const num = parseInt(ageVal, 10);
                if (num < 13) {
                    setErrors(prev => ({ ...prev, age: 'You must be 13 or older to use NatureNani.' }));
                    return;
                }
                if (num > 120) {
                    setErrors(prev => ({ ...prev, age: 'Please enter a valid age.' }));
                    return;
                }
            }
        }

        const answer = answers[currentQuestion.id];

        // ═══════════════════════════════════════════════════════════════════════
        // WOMEN'S HEALTH DYNAMIC FLOW
        // ═══════════════════════════════════════════════════════════════════════
        
        // 1. Inject or Remove questions based on sex selection
        if (currentQuestion.id === 'age_sex') {
            const whIds = ['life_stage', 'menstrual_pattern', 'hormonal_context', 'flow_color'];
            const whQs = whIds
                .map(id => COMPLETE_ONBOARDING_QUESTIONS.find(q => q.id === id))
                .filter((q): q is OnboardingQuestion => !!q);
            
            const newFlow = [...questionFlow];
            const hasWh = newFlow.some(q => whIds.includes(q.id));

            if (answer?.sex === 'female') {
                if (!hasWh) {
                    // Inject after medical_history or age_sex
                    const medicalIdx = newFlow.findIndex(q => q.id === 'medical_history');
                    const insertIdx = medicalIdx !== -1 ? medicalIdx + 1 : currentQuestionIndex + 1;
                    newFlow.splice(insertIdx, 0, ...whQs);
                    setQuestionFlow(newFlow);
                }
            } else {
                if (hasWh) {
                    // Remove if previously injected
                    const filteredFlow = newFlow.filter(q => !whIds.includes(q.id));
                    setQuestionFlow(filteredFlow);

                    // Clear answers to avoid persisting stale women's health data
                    const updatedAnswers = { ...answers };
                    whIds.forEach(id => {
                        delete updatedAnswers[id];
                    });
                    setAnswers(updatedAnswers);
                }
            }
        }

        // 2. Hide or Show Menstrual Pattern based on life stage
        if (currentQuestion.id === 'life_stage') {
            const hideMenstrualPattern = ['pregnant', 'postpartum', 'menopause'].includes(answer);
            const hasMenstrualPattern = questionFlow.some(q => q.id === 'menstrual_pattern');

            if (hideMenstrualPattern && hasMenstrualPattern) {
                const newFlow = questionFlow.filter(q => q.id !== 'menstrual_pattern');
                setQuestionFlow(newFlow);

                // Clear answer if it was previously set
                const updatedAnswers = { ...answers };
                delete updatedAnswers['menstrual_pattern'];
                setAnswers(updatedAnswers);
            } else if (!hideMenstrualPattern && !hasMenstrualPattern) {
                // Re-inject Menstrual Pattern if user moves from 'pregnant' back to 'reproductive'
                const menstrualPatternQ = COMPLETE_ONBOARDING_QUESTIONS.find(q => q.id === 'menstrual_pattern')!;
                const newFlow = [...questionFlow];
                const lifeStageIdx = newFlow.findIndex(q => q.id === 'life_stage');
                if (lifeStageIdx !== -1) {
                    newFlow.splice(lifeStageIdx + 1, 0, menstrualPatternQ);
                    setQuestionFlow(newFlow);
                }
            }
        }

        // Conditional follow-ups
        if (currentQuestion.followUp && currentQuestion.followUp[answer]) {
            const followUpId = currentQuestion.followUp[answer];
            const followUpQuestion = COMPLETE_ONBOARDING_QUESTIONS.find(q => q.id === followUpId);

            if (followUpQuestion && !questionFlow.slice(0, currentQuestionIndex + 1).includes(followUpQuestion)) {
                const newFlow = [...questionFlow];
                newFlow.splice(currentQuestionIndex + 1, 0, followUpQuestion);
                setQuestionFlow(newFlow);
            }
        }

        if (currentQuestionIndex < questionFlow.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
            setShowTeaching(false);
        } else {
            // Calculate data first
            const patternData: Record<string, any> = {};
            const passportData: Record<string, any> = {};

            const whIds = ['life_stage', 'menstrual_pattern', 'hormonal_context', 'flow_color'];
            COMPLETE_ONBOARDING_QUESTIONS.forEach(q => {
                const val = answers[q.id];
                const isInFlow = questionFlow.some(fq => fq.id === q.id);

                if (val && !q.id.startsWith('_transition')) {
                    if (q.category === 'pattern') {
                        patternData[q.id] = val;
                    } else if (q.category === 'passport') {
                        passportData[q.id] = val;
                    }
                } else if (whIds.includes(q.id) && !isInFlow && (segment === 'passport' || segment === 'all')) {
                    // Force null for Women's Health fields not in current flow to clear stale DB entries
                    passportData[q.id] = null;
                }
            });

            const callbackData = { 
                answers, 
                patternData, 
                passportData, 
                vikritiSkipped: vikritiSkipped || localVikritiSkipped 
            };

            // Check WHICH segment just finished
            if (segment === 'primary-goal' || segment === 'passport') {
                onPrakritiComplete?.(callbackData); 
            } else if (segment === 'prakriti') {
                onPrakritiComplete?.(callbackData);
            } else if (segment === 'vikriti-transition') {
                onVikritiComplete?.(callbackData); 
            } else if (segment === 'vikriti' || segment === 'vikriti-questions' || segment === 'all') {
                onVikritiComplete?.(callbackData);
            }

            onComplete({ 
                patternData, 
                passportData, 
                vikritiSkipped: vikritiSkipped || localVikritiSkipped, 
                answers 
            });
        }
    };

    const handleBack = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(currentQuestionIndex - 1);
            setShowTeaching(false);
        }
    };

    const isAnswered = () => {
        const answer = answers[currentQuestion?.id];
        if (!answer) return false;
        if (Array.isArray(answer)) return answer.length > 0;
        if (typeof answer === 'object') return Object.keys(answer).length > 0;
        return true;
    };

    if (!currentQuestion) return null;

    // Check if this is a transition message (centered UI)
    const isTransition = currentQuestion.type === 'transition' || currentQuestion.id.startsWith('_transition');

    return (
        <div className="w-full max-w-2xl mx-auto p-4 md:p-6 font-sans">
            {/* Progress Bar */}
            <div className="mb-10">
                <div className="flex justify-between items-end mb-4">
                    <div className="flex gap-2 items-start w-full max-w-[480px]">
                        {/* Segment 0: Essential Details */}
                        <div 
                            onClick={() => !isRefreshMode && (completedSegments.includes('passport') || segment === 'passport') && onSegmentNavigate?.('passport')}
                            className={`flex-1 ${(!isRefreshMode && (completedSegments.includes('passport') || segment === 'passport')) ? 'cursor-pointer group' : 'opacity-40'}`}
                        >
                            <div className="flex items-center gap-1.5 mb-1.5">
                                <span className={`text-[9px] uppercase tracking-[0.15em] font-black whitespace-nowrap ${segment === 'passport' ? 'text-gray-800' : 'text-gray-400'} group-hover:text-gray-600`}>
                                    S0: Essential details
                                </span>
                                {completedSegments.includes('passport') && <span className="text-gray-500 text-[10px]">✓</span>}
                            </div>
                            <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden border border-gray-200/50">
                                <motion.div 
                                    className="h-full bg-gray-400"
                                    initial={{ width: 0 }}
                                    animate={{ width: (completedSegments.includes('passport') && segment !== 'passport') ? '100%' : (segment === 'passport' ? `${currentSegmentProgress}%` : '0%') }}
                                />
                            </div>
                        </div>

                        {/* Divider */}
                        <div className="h-8 w-px bg-gray-200 mt-2 mx-0.5"></div>

                        {/* Segment 1: Current State */}
                        <div 
                            onClick={() => (completedSegments.includes('vikriti') || skippedSegments.includes('vikriti') || segment?.startsWith('vikriti')) && onSegmentNavigate?.('vikriti-transition')}
                            className={`flex-1 ${(completedSegments.includes('vikriti') || skippedSegments.includes('vikriti') || segment?.startsWith('vikriti')) ? 'cursor-pointer group' : 'opacity-40'}`}
                        >
                            <div className="flex items-center gap-1.5 mb-1.5">
                                <span className={`text-[9px] uppercase tracking-[0.15em] font-black whitespace-nowrap ${segment?.startsWith('vikriti') ? 'text-amber-800' : 'text-gray-400'} group-hover:text-amber-700`}>
                                    S1: Current state
                                </span>
                                {completedSegments.includes('vikriti') && <span className="text-amber-600 text-[10px]">✓</span>}
                                {(skippedSegments.includes('vikriti') || vikritiSkipped) && <span className="text-gray-400 text-[10px]">—</span>}
                            </div>
                            <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden border border-gray-200/50">
                                <motion.div 
                                    className="h-full bg-amber-600"
                                    initial={{ width: 0 }}
                                    animate={{ width: (completedSegments.includes('vikriti') && !segment?.startsWith('vikriti')) ? '100%' : (segment?.startsWith('vikriti') ? `${currentSegmentProgress}%` : '0%') }}
                                />
                            </div>
                        </div>

                        {/* Divider */}
                        <div className="h-8 w-px bg-gray-200 mt-2 mx-0.5"></div>

                        {/* Segment 2: Your Nature */}
                        <div 
                            onClick={() => !isRefreshMode && (completedSegments.includes('prakriti') || segment === 'prakriti') && onSegmentNavigate?.('prakriti-transition')}
                            className={`flex-1 ${(!isRefreshMode && (completedSegments.includes('prakriti') || segment === 'prakriti')) ? 'cursor-pointer group' : 'opacity-40'}`}
                        >
                            <div className="flex items-center gap-1.5 mb-1.5">
                                <span className={`text-[9px] uppercase tracking-[0.15em] font-black whitespace-nowrap ${segment === 'prakriti' ? 'text-emerald-800' : 'text-gray-400'} group-hover:text-emerald-700`}>
                                    S2: Your nature
                                </span>
                                {completedSegments.includes('prakriti') && <span className="text-emerald-600 text-[10px]">✓</span>}
                            </div>
                            <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden border border-gray-200/50">
                                <motion.div 
                                    className="h-full bg-emerald-600"
                                    initial={{ width: 0 }}
                                    animate={{ width: (completedSegments.includes('prakriti') && segment !== 'prakriti') ? '100%' : (segment === 'prakriti' ? `${currentSegmentProgress}%` : '0%') }}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="text-right pl-4">
                        <span className="text-[10px] uppercase tracking-widest text-gray-400 font-bold block mb-1">Impact</span>
                        <span className="text-2xl font-serif font-black text-gray-900 leading-none">
                            {overallProgress}%
                        </span>
                    </div>
                </div>
            </div>



            {/* Transition Message */}
            {currentQuestion.type === 'transition' && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-gradient-to-br from-sage-50 to-earth-50 rounded-3xl p-8 border-2 border-sage-200 shadow-sm"
                >
                    <div className="text-center mb-6">
                        <span className="text-6xl mb-4 block filter drop-shadow-sm">
                            {currentQuestion.id === '_transition_medical' ? '🌿' : (currentQuestion.id === '_transition_prakriti' ? '' : '🔍')}
                        </span>
                        {currentQuestion.id === '_transition_prakriti' && (
                            <div className="flex justify-center mb-4">
                                <div className="p-4 bg-emerald-50 rounded-full">
                                    <TreePine size={48} className="text-emerald-600" />
                                </div>
                            </div>
                        )}
                        <h2 className="text-3xl font-serif font-bold text-sage-900 mb-3">
                            {currentQuestion.id === '_transition_prakriti' 
                                ? "Now let's discover your lifelong nature (Prakriti)" 
                                : currentQuestion.question}
                        </h2>
                    </div>

                    <div className="bg-white rounded-2xl p-6 mb-8 shadow-sm border border-sage-100">
                        <p className="text-sage-800 leading-relaxed mb-4">
                            {currentQuestion.teaching}
                        </p>
                        
                        {(currentQuestion.id === '_transition_vikriti' || currentQuestion.id === '_transition_prakriti') && (
                             <div className="p-4 bg-sage-50 rounded-xl border border-sage-200 text-sm text-sage-700 italic">
                                {currentQuestion.id === '_transition_prakriti'
                                    ? "Every person is born with a unique constitutional blueprint. Understanding yours is the key to lasting balance."
                                    : "In Ayurveda, we look at both your lifelong nature (Prakriti) and your current state of imbalance (Vikriti). This helps us see the gap between who you are and how you feel."}
                             </div>
                        )}
                        
                        {currentQuestion.bullets && (
                            <div className="space-y-3 text-sm text-sage-700 font-medium mt-4">
                                {currentQuestion.bullets.map((bullet, idx) => (
                                    <div key={idx} className="flex items-start gap-3">
                                        <span className="text-sage-500 bg-sage-100 rounded-full p-1 w-5 h-5 flex items-center justify-center text-[10px]">✓</span>
                                        <span>{bullet}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="space-y-4">
                        {currentQuestion.id === '_transition_vikriti' ? (
                            <div className="space-y-4">
                                <button
                                    onClick={handleNext}
                                    className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold font-serif py-4 px-8 rounded-2xl shadow-lg transition-all transform hover:scale-[1.02]"
                                >
                                    Add a health concern →
                                </button>
                                {userIntent === 'wellness' && (
                                 <button
                                    onClick={() => {
                                        setLocalVikritiSkipped(true);
                                        const callbackData = {
                                            answers: { ...answers, vikriti_decision: 'skip' },
                                            patternData: {},
                                            passportData: {},
                                            vikritiSkipped: true
                                        };
                                        try { 
                                            onVikritiComplete?.(callbackData); 
                                            // Auto-advance if we're in 'all' segment mode or similar
                                            if (currentQuestionIndex < questionFlow.length - 1) {
                                                setCurrentQuestionIndex(currentQuestionIndex + 1);
                                            }
                                        } catch (err) { logger.error('Vikriti callback failed', err); }
                                    }}
                                    className="w-full text-sage-500 hover:text-sage-700 text-sm font-medium transition-colors underline decoration-dotted underline-offset-4 text-center"
                                >
                                    {completedSegments.includes('vikriti') ? 'Skip this section (using previous answers)' : 'Skip for now'}
                                </button>
                                )}
                            </div>
                        ) : currentQuestion.id === '_transition_prakriti' ? (
                            <div className="space-y-4">
                                <button
                                    onClick={handleNext}
                                    className="w-full bg-sage-600 hover:bg-sage-700 text-white font-bold font-serif py-4 px-8 rounded-2xl shadow-lg transition-all transform hover:scale-[1.02]"
                                >
                                    Start questionnaire
                                </button>
                                 <button
                                    onClick={() => {
                                        // Skip Prakriti -> Finish the whole flow
                                        const pData: Record<string, any> = {};
                                        const passData: Record<string, any> = {};
                                        COMPLETE_ONBOARDING_QUESTIONS.forEach(q => {
                                            const val = answers[q.id];
                                            if (val && !q.id.startsWith('_transition')) {
                                                if (q.category === 'pattern') {
                                                    pData[q.id] = val;
                                                } else if (q.category === 'passport') {
                                                    passData[q.id] = val;
                                                }
                                            }
                                        });

                                        onComplete({
                                            answers,
                                            patternData: pData,
                                            passportData: passData,
                                            vikritiSkipped: vikritiSkipped || localVikritiSkipped,
                                            prakritiSkipped: true
                                        });
                                    }}
                                    className="w-full text-sage-500 hover:text-sage-700 text-sm font-medium transition-colors underline decoration-dotted underline-offset-4 text-center"
                                >
                                    {completedSegments.includes('prakriti') ? 'Skip this section (current pattern is saved)' : 'Skip for now'}
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={handleNext}
                                className="w-full bg-sage-600 hover:bg-sage-700 text-white font-bold font-serif py-4 px-8 rounded-2xl shadow-lg transition-all transform hover:scale-[1.02]"
                            >
                                {currentQuestion.id === '_transition_medical' ? "Let's begin →" : "Continue →"}
                            </button>
                        )}
                    </div>
                </motion.div>
            )}

            {/* Regular Question Card */}
            {!isTransition && currentQuestion && (
                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentQuestion.id}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                        className="bg-white rounded-3xl shadow-lg shadow-sage-900/5 p-6 md:p-8 border border-sage-100"
                    >
                        {/* Fix 7: Section Label for Vikriti */}
                        {segment === 'vikriti-questions' && currentQuestionIndex === 0 && (
                            <div className="mb-6 py-2 px-4 bg-amber-50 rounded-xl border border-amber-100 inline-block">
                                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-700">Section: Your current state (Vikriti)</span>
                            </div>
                        )}

                        {/* Question Header */}
                        <h2 className="text-3xl font-serif font-bold text-sage-900 mb-4">
                            {currentQuestion.id === 'primary_goal' 
                                ? (userIntent === 'wellness' ? 'What aspect of your wellbeing matters most to you?' : 'What is your primary health goal?')
                                : currentQuestion.question}
                        </h2>
                        
                        {currentQuestion.id === '_transition_medical' ? (
                            <p className="text-sage-700 leading-relaxed mb-8 text-lg font-medium">
                                Before we begin, a few quick details help Nani personalise your guidance safely. 🌿
                            </p>
                        ) : (
                            <p className="text-sage-700 leading-relaxed mb-8 text-lg font-medium italic">
                                "{currentQuestion.teaching}"
                            </p>
                        )}
                        <div className="mb-8">
                            {/* Demographics Input */}
                            {currentQuestion.type === 'demographics' && (
                                <DemographicsInput
                                    fields={currentQuestion.fields!}
                                    value={answers[currentQuestion.id]}
                                    onChange={(val: any) => handleAnswer(val)}
                                    onErrorChange={(fieldId: string, error: string | null) => {
                                        setErrors(prev => ({ ...prev, [fieldId]: error || '' }));
                                    }}
                                    externalErrors={errors}
                                />
                            )}

                            {/* Body Metrics Input */}
                            {currentQuestion.type === 'body-metrics' && (
                                <BodyMetricsInput
                                    fields={currentQuestion.fields!}
                                    value={answers[currentQuestion.id]}
                                    onChange={(val: any) => handleAnswer(val)}
                                    onErrorChange={(fieldId: string, error: string | null) => {
                                        setErrors(prev => ({ ...prev, [fieldId]: error || '' }));
                                    }}
                                    externalErrors={errors}
                                />
                            )}

                            {/* Multi Input (for allergies, medications) */}
                            {currentQuestion.type === 'multi-input' && (
                                <MultiInput
                                    placeholder={currentQuestion.placeholder!}
                                    value={answers[currentQuestion.id] || []}
                                    onChange={(val: any) => handleAnswer(val)}
                                />
                            )}

                            {/* Single Select Options */}
                            {(currentQuestion.type === 'single-select' ||
                                currentQuestion.type === 'timeline' ||
                                currentQuestion.type === 'scale') && (
                                    <div className="space-y-3">
                                        {(currentQuestion.id === 'primary_goal' ? (
                                            userIntent === 'wellness' ? [
                                                { value: 'prakriti-understanding', label: 'Understand my body type (Prakriti)', emoji: '🧬' },
                                                { value: 'constitutional-nutrition', label: 'Eat right for my constitution', emoji: '🥗' },
                                                { value: 'sustainable-energy', label: 'Build sustainable energy', emoji: '⚡' },
                                                { value: 'sleep-optimization', label: 'Improve sleep quality', emoji: '😴' },
                                                { value: 'mental-clarity', label: 'Manage stress and mental clarity', emoji: '🧘' },
                                                { value: 'healthy-ageing', label: 'Healthy ageing and prevention', emoji: '⏳' },
                                                { value: 'hormonal-vitality', label: 'Hormonal balance and vitality', emoji: '🌸' },
                                                { value: 'metabolism-weight', label: 'Weight and metabolism', emoji: '⚖️' },
                                            ] : [
                                                { value: 'chronic-condition-management', label: 'Manage a chronic condition', emoji: '🩹' },
                                                { value: 'digestive-disorders', label: 'Digestive disorders (IBS, acidity, bloating)', emoji: '🌪️' },
                                                { value: 'joint-pain-arthritis', label: 'Joint pain or arthritis', emoji: '🦴' },
                                                { value: 'cardiovascular-health', label: 'Cardiovascular health (BP, cholesterol)', emoji: '❤️' },
                                                { value: 'metabolic-thresholds', label: 'Metabolic conditions (diabetes, thyroid)', emoji: '🦋' },
                                                { value: 'skin-conditions', label: 'Skin conditions (acne, eczema, psoriasis)', emoji: '🍎' },
                                                { value: 'respiratory-health', label: 'Respiratory health (asthma, allergies)', emoji: '🫁' },
                                                { value: 'mental-health-support', label: 'Mental health support (anxiety, sleep)', emoji: '🧠' },
                                            ]
                                        ) : currentQuestion.options!).map((option) => {
                                            const isSelected = answers[currentQuestion.id] === option.value;

                                            return (
                                                <motion.button
                                                    key={option.value}
                                                    onClick={() => handleAnswer(option.value)}
                                                    className={`w-full text-left p-4 rounded-2xl border-2 transition-all group
                          ${isSelected
                                                            ? 'border-sage-500 bg-sage-50 shadow-sm'
                                                            : 'border-gray-200 hover:border-earth-300 hover:bg-earth-50'
                                                        }`}
                                                    whileHover={{ scale: 1.01 }}
                                                    whileTap={{ scale: 0.99 }}
                                                >
                                                    <div className="flex items-center gap-4">
                                                        {option.emoji && <span className="text-3xl group-hover:scale-110 transition-transform">{option.emoji}</span>}
                                                        <div className="flex-1">
                                                            <p className="font-bold text-sage-800">{option.label}</p>
                                                            {option.description && (
                                                                <p className="text-sm text-sage-600 mt-0.5">{option.description}</p>
                                                            )}
                                                        </div>
                                                        {isSelected && (
                                                            <motion.div
                                                                initial={{ scale: 0 }}
                                                                animate={{ scale: 1 }}
                                                                className="w-6 h-6 bg-sage-500 rounded-full flex items-center justify-center shrink-0"
                                                            >
                                                                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                                </svg>
                                                            </motion.div>
                                                        )}
                                                    </div>
                                                </motion.button>
                                            );
                                        })}

                                        {/* Removed Something else free-text field for Primary Goal as per request */}
                                    </div>
                                )}

                            {/* Multi Select Options */}
                            {currentQuestion.type === 'multi-select' && (
                                <div className="space-y-3">
                                    {currentQuestion.options!.map((option) => {
                                        const isSelected = (answers[currentQuestion.id] || []).includes(option.value);

                                        return (
                                            <motion.button
                                                key={option.value}
                                                onClick={() => handleAnswer(option.value, true)}
                                                className={`w-full text-left p-4 rounded-2xl border-2 transition-all group
                          ${isSelected
                                                        ? 'border-sage-500 bg-sage-50 shadow-sm'
                                                        : 'border-gray-200 hover:border-earth-300 hover:bg-earth-50'
                                                    }`}
                                                whileHover={{ scale: 1.01 }}
                                                whileTap={{ scale: 0.99 }}
                                            >
                                                <div className="flex items-center gap-4">
                                                    {option.emoji && <span className="text-3xl group-hover:scale-110 transition-transform">{option.emoji}</span>}
                                                    <div className="flex-1">
                                                        <p className="font-bold text-sage-800">{option.label}</p>
                                                    </div>
                                                    <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center shrink-0 transition-colors
                            ${isSelected ? 'bg-sage-500 border-sage-500' : 'border-gray-300 bg-white'}`}
                                                    >
                                                        {isSelected && (
                                                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                </div>
                                            </motion.button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Teaching Moment */}
                        <AnimatePresence>
                            {showTeaching && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0 }}
                                    className="p-5 bg-earth-50 rounded-2xl border-l-4 border-earth-400 mb-8 shadow-sm"
                                >
                                    <div className="flex gap-4">
                                        <span className="text-2xl pt-1">💡</span>
                                        <div>
                                            <p className="font-bold text-earth-900 mb-1 font-serif">Did you know?</p>
                                            <p className="text-sm text-earth-800 leading-relaxed font-medium">{currentQuestion.teaching}</p>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Navigation Buttons */}
                        <div className="flex gap-4 pt-4 border-t border-gray-100">
                            {currentQuestionIndex > 0 && (
                                <button
                                    onClick={handleBack}
                                    className="px-6 py-3 rounded-2xl border-2 border-gray-200 text-gray-600 
                           font-bold hover:bg-gray-50 hover:border-gray-300 transition-all"
                                >
                                    ← Back
                                </button>
                            )}

                            {currentQuestion.allowSkip && !isAnswered() && (
                                <button
                                    onClick={handleNext}
                                    className="px-6 py-3 rounded-2xl border-2 border-gray-200 text-gray-500 
                           hover:bg-gray-50 hover:text-gray-700 transition-all font-medium"
                                >
                                    {currentQuestion.category === 'pattern' && !currentQuestion.prakriti_factor 
                                        ? "Skip for now — I'll add a concern later"
                                        : "Skip for now"}
                                </button>
                            )}


                            <button
                                onClick={handleNext}
                                disabled={(!isAnswered() && !currentQuestion.allowSkip) || Object.values(errors).some(e => e !== '')}
                                className={`flex-1 px-6 py-3 rounded-2xl font-bold font-serif transition-all shadow-md
                  ${(isAnswered() || currentQuestion.allowSkip) && !Object.values(errors).some(e => e !== '')
                                        ? 'bg-sage-600 text-white hover:bg-sage-700 hover:shadow-lg transform hover:scale-[1.02]'
                                        : 'bg-gray-100 text-gray-400 cursor-not-allowed shadow-none'
                                    }`}
                            >
                                 {currentQuestionIndex === questionFlow.length - 1
                                     ? (segment === 'all' || segment === 'prakriti' 
                                         ? (currentQuestion.id === 'primary_goal' ? (userIntent === 'wellness' ? 'Begin my journey →' : 'Complete Setup →') : 'Complete Setup →')
                                         : 'Continue to next section →')
                                     : 'Next Question →'}
                            </button>
                        </div>
                    </motion.div>
                </AnimatePresence>
            )}

            {/* Progress Summary */}
            {Object.keys(answers).length > 3 && !isTransition && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="mt-8 p-6 bg-sage-50/50 rounded-3xl border border-sage-100/50"
                >
                    <div className="flex items-center gap-2 mb-3">
                        <span className="text-sm font-bold text-sage-600 uppercase tracking-widest">Your responses so far</span>
                        <span className="w-full h-px bg-sage-200"></span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {Object.keys(answers).filter(k => k !== '_transition_medical').slice(0, 5).map((questionId) => (
                            <span
                                key={questionId}
                                className="px-3 py-1 bg-white rounded-lg text-xs font-bold text-sage-700 border border-sage-200 shadow-sm uppercase tracking-wide"
                            >
                                {questionId.replace(/_/g, ' ')}
                            </span>
                        ))}
                        {Object.keys(answers).length > 5 && (
                            <span className="px-3 py-1 bg-sage-100 rounded-lg text-xs font-bold text-sage-700 border border-sage-200">
                                +{Object.keys(answers).length - 5} more
                            </span>
                        )}
                    </div>
                </motion.div>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS FOR DIFFERENT INPUT TYPES
// ═══════════════════════════════════════════════════════════════════════

function DemographicsInput({ fields, value = {}, onChange, onErrorChange, externalErrors = {} }: any) {
    const [formData, setFormData] = React.useState(value);

    const handleFieldChange = (fieldId: string, fieldValue: any) => {
        if (fieldId === 'age' && typeof fieldValue === 'string' && fieldValue !== '') {
            const num = parseFloat(fieldValue);
            if (!isNaN(num)) {
                fieldValue = Math.floor(num).toString();
            }
        }

        const newData = { ...formData, [fieldId]: fieldValue };
        setFormData(newData);
        onChange(newData);

        if (onErrorChange && externalErrors[fieldId]) {
            onErrorChange(fieldId, null);
        }
    };

    const handleBlur = (fieldId: string) => {
        if (fieldId === 'age') {
            const val = formData[fieldId];
            if (val !== undefined && val !== null && val !== '') {
                const num = parseInt(val, 10);
                let err = '';
                if (num < 13) {
                    err = 'You must be 13 or older to use NatureNani.';
                } else if (num > 120) {
                    err = 'Please enter a valid age.';
                }
                
                if (onErrorChange && err !== externalErrors[fieldId]) {
                    onErrorChange(fieldId, err);
                }
            }
        }
    };

    return (
        <div className="space-y-4">
            {fields.map((field: any) => (
                <div key={field.id}>
                    <label className="block text-sm font-bold text-sage-700 mb-2 uppercase tracking-wide">
                        {field.label}
                    </label>
                    {field.type === 'select' ? (
                        <select
                            value={formData[field.id] || ''}
                            onChange={(e) => handleFieldChange(field.id, e.target.value)}
                            className="w-full px-4 py-4 rounded-2xl border-2 border-gray-200 
                       focus:border-sage-500 focus:outline-none bg-white font-medium text-gray-700"
                        >
                            <option value="">Select...</option>
                            {field.options.map((opt: any) => {
                                const val = typeof opt === 'string' ? opt.toLowerCase() : opt.value;
                                const label = typeof opt === 'string' ? opt : opt.label;
                                return <option key={val} value={val}>{label}</option>;
                            })}
                        </select>
                    ) : (
                        <div className="space-y-1">
                            <input
                                type={field.type}
                                value={formData[field.id] || ''}
                                onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                onBlur={() => handleBlur(field.id)}
                                placeholder={field.placeholder}
                                className={`w-full px-4 py-4 rounded-2xl border-2 transition-all font-medium text-gray-700 placeholder-gray-400 focus:outline-none 
                                ${externalErrors[field.id] ? 'border-red-300 focus:border-red-400' : 'border-gray-200 focus:border-sage-500'}`}
                            />
                            {externalErrors[field.id] && <p className="text-red-500 text-sm font-medium ml-1">{externalErrors[field.id]}</p>}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

function BodyMetricsInput({ fields, value = {}, onChange, onErrorChange, externalErrors = {} }: any) {
    const [formData, setFormData] = React.useState(value);

    const handleFieldChange = (fieldId: string, fieldValue: any) => {
        const newData = { ...formData, [fieldId]: fieldValue };
        setFormData(newData);
        onChange(newData);

        // Clear error when typing
        if (onErrorChange && externalErrors[fieldId]) {
            onErrorChange(fieldId, null);
        }
    };

    const handleBlur = (fieldId: string) => {
        const val = formData[fieldId];
        if (val === undefined || val === null || val === '') return;
        
        const num = parseFloat(val);
        let err = '';

        // Fix 8: Range validation for height and weight
        if (fieldId === 'height') {
            if (num < 50 || num > 300) err = 'Please enter a height between 50 and 300 cm.';
        } else if (fieldId === 'weight') {
            if (num < 20 || num > 500) err = 'Please enter a weight between 20 and 500 kg.';
        }

        if (onErrorChange && err !== externalErrors[fieldId]) {
            onErrorChange(fieldId, err);
        }
    };

    return (
        <div className="grid grid-cols-2 gap-4">
            {fields.map((field: any) => (
                <div key={field.id}>
                    <label className="block text-sm font-bold text-sage-700 mb-2 uppercase tracking-wide">
                        {field.label}
                    </label>
                    <div className="space-y-1">
                        <div className="relative">
                            <input
                                type="number"
                                value={formData[field.id] || ''}
                                onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                onBlur={() => handleBlur(field.id)}
                                placeholder={field.placeholder}
                                className={`w-full px-4 py-4 pr-12 rounded-2xl border-2 transition-all font-medium text-gray-700 focus:outline-none placeholder-gray-400
                                    ${externalErrors[field.id] ? 'border-red-300 focus:border-red-400' : 'border-gray-200 focus:border-sage-500'}`}
                            />
                            {field.unit && (
                                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">
                                    {field.unit}
                                </span>
                            )}
                        </div>
                        {externalErrors[field.id] && <p className="text-red-500 text-xs font-medium ml-1">{externalErrors[field.id]}</p>}
                    </div>
                </div>
            ))}
        </div>
    );
}

function MultiInput({ placeholder, value = [], onChange }: any) {
    const [inputValue, setInputValue] = React.useState('');
    const [items, setItems] = React.useState<string[]>(value);

    const handleAdd = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && inputValue.trim()) {
            e.preventDefault();
            const newItems = [...items, inputValue.trim()];
            setItems(newItems);
            onChange(newItems);
            setInputValue('');
        }
    };

    const handleRemove = (index: number) => {
        const newItems = items.filter((_, i) => i !== index);
        setItems(newItems);
        onChange(newItems);
    };

    return (
        <div>
            <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleAdd}
                placeholder={placeholder}
                className="w-full px-4 py-4 rounded-2xl border-2 border-gray-200 
                 focus:border-sage-500 focus:outline-none mb-4 placeholder-gray-400 font-medium text-gray-700"
            />
            {items.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {items.map((item, index) => (
                        <span
                            key={index}
                            className="bg-sage-100 text-sage-800 px-3 py-1.5 rounded-lg flex items-center gap-2 font-bold text-sm border border-sage-200"
                        >
                            {item}
                            <button
                                onClick={() => handleRemove(index)}
                                className="text-sage-400 hover:text-sage-600 focus:outline-none"
                            >
                                ✕
                            </button>
                        </span>
                    ))}
                </div>
            )}
            {items.length === 0 && (
                <p className="text-sm text-gray-400 italic pl-1">Press Enter to add multiple items</p>
            )}
        </div>
    );
}
